<template>
  <base-wrapper :loading="loadingStockistCenter || loadingJobStatus">
    <div class="space-y-4">
      <div class="flex justify-end gap-2">
        <base-input
          type="search"
          placeholder="Cari Stockist Center"
          debounce
          :shadow="false"
          v-model="filter.search"
          @native-input="loadStockistCenter"
        />
        <base-button :loading="loadingExport.visible" @click="onExport">
          <Icon
            v-if="!loadingExport.visible"
            icon="heroicons:arrow-down-tray"
            class="h-4 w-4"
          />
          {{ loadingExport.visible ? loadingExport.text : 'Export' }}
        </base-button>
      </div>
      <datatable
        :total="summary.total"
        :perPage="stockistCenters.meta.page.perPage"
        :currentPage="currentPage"
        :meta="stockistCenters.meta"
        :columns="[
          { id: 'code', name: 'Kode Stockist Center' },
          { id: 'name', name: 'Nama Stockist Center' },
          { id: 'area', name: 'Area' },
          { id: 'phone', name: 'Alamat / No. Telp' },
        ]"
        cursor
        @pagechanged="onPageChange"
        @prev="currentPage--"
        @next="currentPage++"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="stockistCenter in stockistCenters.data"
            :key="stockistCenter.id"
            :class="[classes.tr, 'cursor-pointer bg-white hover:bg-green-100']"
            @click="onClickDetail(stockistCenter)"
          >
            <td :class="classes.td">
              <p class="font-bold text-gray-900">
                {{ stockistCenter.attributes.code }}
              </p>
            </td>
            <td :class="classes.td">
              <p class="text-gray-900 mb-1">{{ stockistCenter.attributes.name }}</p>
              <p class="text-xs">{{ getOfficeIncluded(stockistCenter).attributes.email ?? '' }}</p>
            </td>
            <td :class="classes.td">{{ getOfficeIncluded(stockistCenter).attributes.area_name ?? '-' }}</td>
            <td :class="classes.td">
              <div>
                <p>
                  {{ getOfficeIncluded(stockistCenter).attributes.full_address ?? '-' }}
                </p>
                <p class="text-xs">
                  {{ getOfficeIncluded(stockistCenter).attributes.phone ?? '' }}
                </p>
              </div>
            </td>
          </tr>
        </template>
      </datatable>
    </div>

    <view-office-modal
      office-type="stockist_center"
      :loading="detailModal.loading"
      :visible="detailModal.visible"
      :office-id="detailModal.officeId"
      editable
      edit-route="master.stockist-center.edit"
      :columns="['origin_member', 'origin_stockist']"
      @close="detailModal.visible = false"
    />
  </base-wrapper>
</template>

<script>
import ViewOfficeModal from '@/components/office/view-office-modal.vue';
import { requestMixin } from '@/mixins/request/request';
import { mapActions, mapGetters } from 'vuex';
import { downloadFileUrl } from '../../../../../services/utils.service';

export default {
  name: 'Home',
  mixins: [requestMixin],
  components: {
    ViewOfficeModal,
  },
  data() {
    return {
      currentPage: 1,
      detailModal: {
        officeId: null,
        visible: false,
        loading: false
      },
      filter: {
        search: null,
      },
      loadingStockistCenter: false,
      loadingJobStatus: false,
      loadingExport: {
        visible: false,
        text: null,
      },
      stockistCenters: {
        data: [],
        meta: {
          page: {},
        },
      },
      summary: {}
    };
  },
  computed: {
    ...mapGetters({
      getChannelName: 'auth/getChannelName',
    }),
  },
  created() {
    this.loadJobStatus();
    this.loadStockistCenter();
    this.addListener();
  },
  beforeRouteLeave(to, from, next) {
    this.removeListener();
    next();
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    addListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onExportProgress
      );
    },
    async onClickDetail(simpleOffice) {
      this.detailModal.officeId = simpleOffice.relationships.office.data.id;
      this.detailModal.visible = true;
    },
    getOfficeIncluded(simpleOffice) {
      return this.getSingleIncluded(
        this.stockistCenters,
        simpleOffice.relationships.office.data.id
      );
    },
    onPageChange(page) {
      this.loadStockistCenter({
        ...page,
        'filter[with_summary]': false
      });
    },
    async loadJobStatus() {
      this.loadingJobStatus = true;

      const [res, err] = await this.request(
        '/api/v1/users/-actions/check-job-status',
        {
          params: {
            type: 'export_pin_stockist_center',
          },
        }
      );

      if (!err) {
        const { running } = res;

        if (running) {
          this.startLoadingExport('Sedang Diproses');
        }
      }

      this.loadingJobStatus = false;
    },
    async loadStockistCenter(params) {
      this.loadingStockistCenter = true;

      const [res, err] = await this.request('/api/v1/offices', {
        params: {
          'page[limit]': 5,
          'filter[office_category_id]': 3,
          include: 'office',
          'fields[simple-offices]': 'code,name,office',
          'fields[offices]': 'full_address,phone,email,area_name',
          'filter[search]': this.filter.search,
          'filter[with_summary]': true,
          ...params,
        },
      });

      if (!err) {
        this.stockistCenters = res;

        if (res.meta.summary) {
          this.summary = res.meta.summary
        }

        if (!params) {
          this.currentPage = 1
        }
      }

      this.loadingStockistCenter = false;
    },
    async onExport() {
      this.startLoadingExport('Mengirim ke server');

      const [, err] = await this.request(
        `/api/v1/offices/-actions/export-pin`,
        {
          method: 'post',
          params: {
            office_type: 'stockist_center',
          },
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });

        this.stopLoadingExport();
      } else {
        this.startLoadingExport('Sedang diproses');
      }
    },
    onExportProgress(e) {
      if (e.type === 'App\\Notifications\\OfficePinExported') {
        if (e.status === 'failed') {
          this.stopLoadingExport();

          this.createAlert({
            data: 'Gagal mengexport stockist center',
            status: 'error',
          });
        } else if (e.status === 'success') {
          this.stopLoadingExport();

          downloadFileUrl(e.file_url, 'stockist_center.csv', { blank: true });
        }
      }
    },
    removeListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onExportProgress
        );
      }
    },
    startLoadingExport(text) {
      this.loadingExport.text = text;
      this.loadingExport.visible = true;
    },
    stopLoadingExport() {
      this.loadingExport.visible = false;
    },
  },
};
</script>
